// ==================== CONFIGURACIÓN DE AXIOS ====================
// Este archivo configura axios para comunicarse con el backend

import axios from 'axios';
import { API_BASE_URL } from '../config/apiConfig';

// URL base del backend - usa la configuración centralizada
const API_URL = API_BASE_URL;
const BACKEND_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

// Crear instancia de axios con configuración base
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 60000, // 60 segundos de timeout (necesario para registro con plantillas)
  validateStatus: (status) => status < 500, // No rechazar automáticamente errores 4xx
});

// ==================== INTERCEPTOR DE REQUESTS ====================
// Se ejecuta ANTES de cada petición para agregar el token de autenticación

api.interceptors.request.use(
  (config) => {
    // Obtener el token del localStorage
    const token = localStorage.getItem('token');

    // Si existe el token, agregarlo al header Authorization
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
      // Cualquier llamada al API cuenta como actividad, para la expiración de sesión por inactividad
      localStorage.setItem('ultimaActividad', Date.now().toString());
    }

    // Inyectar el proyecto activo en cada request para soporte multi-proyecto
    const proyectoActivoId = localStorage.getItem('proyectoActivoId');
    if (proyectoActivoId && !config.headers['X-Proyecto-Id']) {
      config.headers['X-Proyecto-Id'] = proyectoActivoId;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// ==================== INTERCEPTOR DE RESPONSES ====================
// Se ejecuta DESPUÉS de cada respuesta del servidor

api.interceptors.response.use(
  (response) => {
    // Verificar si es un error 4xx antes de retornar
    if (response.status >= 400 && response.status < 500) {
      // Crear un error de axios manualmente
      const error = new Error(`Request failed with status code ${response.status}`);
      error.response = response;
      error.status = response.status;
      
      return Promise.reject(error);
    }
    
    // Si la respuesta es exitosa, simplemente retornarla
    return response;
  },
  async (error) => {
    // const originalRequest = error.config; // Reservado para futura implementación de refresh token
    
    // Manejar errores de red (backend no disponible)
    if (!error.response && error.code === 'ERR_NETWORK') {
      console.error(`❌ Backend no disponible. Verifica la conexión a: ${BACKEND_URL}`);
      
      // Guardar indicador de que el backend está caído
      sessionStorage.setItem('backendDown', 'true');
      
      return Promise.reject(error);
    }
    
    // Limpiar indicador si el backend responde
    sessionStorage.removeItem('backendDown');
    
    // Si el token expiró o es inválido (error 401)
    if (error.response && error.response.status === 401) {
      // Limpiar el localStorage
      localStorage.removeItem('token');
      localStorage.removeItem('usuario');
      localStorage.removeItem('proyectoId');
      localStorage.removeItem('proyectos');
      localStorage.removeItem('proyectoActivoId');
      
      console.error('🔐 Sesión expirada. Por favor inicia sesión nuevamente.');
    }
    
    // Logging mejorado de errores
    if (error.response) {
      console.error(`❌ Error ${error.response.status}:`, error.response.data);
    }
    
    return Promise.reject(error);
  }
);

// ==================== FUNCIÓN DE HEALTH CHECK ====================
// Verificar si el backend está disponible
export const verificarBackend = async () => {
  try {
    const response = await axios.get(BACKEND_URL, { timeout: 3000 });
    sessionStorage.removeItem('backendDown');
    return response.status === 200;
  } catch (error) {
    sessionStorage.setItem('backendDown', 'true');
    return false;
  }
};

// Espera hasta que el backend responda o se agote el tiempo máximo.
// onIntento(n) se llama en cada intento fallido para actualizar la UI.
export const esperarBackend = async (maxMs = 60000, intervaloMs = 5000, onIntento = null) => {
  const inicio = Date.now();
  let intento = 0;
  while (Date.now() - inicio < maxMs) {
    const ok = await verificarBackend();
    if (ok) return true;
    intento++;
    if (onIntento) onIntento(intento);
    await new Promise(r => setTimeout(r, intervaloMs));
  }
  return false;
};

// ==================== WRAPPER CON REINTENTOS ====================
// Ejecutar petición con reintentos automáticos
export const ejecutarConReintentos = async (peticion, maxReintentos = 2) => {
  let ultimoError = null;
  
  for (let intento = 0; intento < maxReintentos; intento++) {
    try {
      const resultado = await peticion();
      return resultado;
    } catch (error) {
      ultimoError = error;
      
      // No reintentar en errores de validación (400) o autenticación (401)
      if (error.response && [400, 401, 403, 404].includes(error.response.status)) {
        throw error;
      }
      
      // Esperar antes de reintentar (backoff exponencial)
      if (intento < maxReintentos - 1) {
        const delay = Math.pow(2, intento) * 500; // 500ms, 1000ms, 2000ms...
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
  }
  
  throw ultimoError;
};

export default api;
